import React from 'react';
import PropTypes from 'prop-types';
import { tokens, useBrsCreateStyles } from '../BrsCreateStyles';
import {
  DAYS,
  computeWeekDates,
  normalizeScheduleForMode,
  parseCellInputForMode,
  pctClass,
} from './splitWeekUtils';

export const SplitWeekSchedule = ({ spot, onChange, unitRotation }) => {
  const classes = useBrsCreateStyles();

  if (!spot) return null;

  const schedule = normalizeScheduleForMode(spot.split_week_schedule, unitRotation);
  const weekDates = computeWeekDates(spot.start_date);

  const handleCellEdit = (dayKey, rawValue) => {
    const parsed = parseCellInputForMode(rawValue, unitRotation);
    if (parsed === null) return;
    const updated = { ...schedule, [dayKey]: parsed };
    const updatedSpot = { ...spot, split_week_schedule: updated };
    // If all days are no longer the same value, clear the unit/rotation field
    // since the single number no longer represents the per-day schedule.
    const vals = DAYS.map((d) => updated[d.key]);
    if (!vals.every((v) => v === vals[0])) {
      updatedSpot.unit = '';
      updatedSpot.rotation = '';
    }
    onChange(updatedSpot);
  };

  const spotTotal = DAYS.reduce((acc, d) => acc + schedule[d.key], 0);

  return (
    <div>
      <table className={classes.timelineGrid}>
        <thead>
          <tr>
            <th>Spot</th>
            {DAYS.map((d, i) => (
              <th key={d.key}>
                {d.label}
                {weekDates[i] && (
                  <>
                    <br />
                    <span style={{ fontSize: 10, fontWeight: 400 }}>{weekDates[i]}</span>
                  </>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <span className={classes.isciMono}>{spot.isciasset?.isci || 'No ISCI'}</span>
              <span className={classes.spotName}>{spot.title || 'Untitled'}</span>
            </td>
            {DAYS.map((d) => {
              const value = schedule[d.key];
              return (
                <td key={d.key}>
                  <div className={`${classes.dayCell} ${pctClass(classes, value)}`}>
                    <input
                      className={classes.dayCellInput}
                      type="number"
                      min="0"
                      {...(unitRotation !== 'unit' ? { max: 100 } : {})}
                      step="1"
                      value={value}
                      onChange={(e) => handleCellEdit(d.key, e.target.value)}
                      aria-label={`${d.label} ${unitRotation === 'unit' ? 'unit count' : 'rotation percent'}`}
                    />
                  </div>
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
      <div className={classes.cycleHint}>
        <span style={{ color: tokens.info }}>&#128161;</span>
        <span>
          {unitRotation === 'unit'
            ? <>Type an integer in any cell to set its unit count. Spot total:{' '}<strong>{spotTotal}</strong> across the week.</>
            : <>Type an integer 0&ndash;100 in any cell to set its rotation percentage. Spot total:{' '}<strong>{spotTotal}%</strong> across the week.</>}
        </span>
      </div>
    </div>
  );
};

SplitWeekSchedule.propTypes = {
  spot: PropTypes.shape({
    title: PropTypes.string,
    start_date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    isciasset: PropTypes.shape({
      isci: PropTypes.string,
    }),
    split_week_schedule: PropTypes.object,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  unitRotation: PropTypes.string,
};
